import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-time-picker-input/dist/components/TimeInput.css';
import * as Yup from 'yup';
import { convertLocalToUtc } from '../../../Helpers';
import TableSelection from '../../common/DataTableViews/Auction/TableSelection';
import dayjs from 'dayjs';
import type { TimePickerProps } from 'antd';
import { TimePicker } from 'antd';
import AuctionTypeSelection from '../../common/DataTableViews/Auction/AuctionTypeSelection';
import { useAuth } from '../../../modules/auth/core/Auth';

function createDateWithTime(date: Date | null, time: string | null) {
	if (!date || !time) return new Date();

	// Parse the time in 12h format (e.g. "5:00 PM")
	const [timePart, modifier] = time.split(' ');
	let [hours, minutes] = timePart.split(':').map(Number);

	// Convert to 24h format
	if (modifier === 'PM' && hours < 12) hours += 12;
	if (modifier === 'AM' && hours === 12) hours = 0;

	// Create new date with the adjusted time
	const newDate = new Date(date);
	newDate.setHours(hours, minutes, 0, 0);

	return newDate;
}

interface TAuction {
	id?: string;
	tableId?: string;
	auctionType?: {
		id: string;
		type: string;
	};
	tablePrice: string;
	price: number;
	deadlineDate?: any;
	dineTime?: any;
	dineDate?: any;
	bidsEndAt?: any;
	isView?: boolean;
	auctionStartDate?: any;
	auctionStartTime?: any;
	auctionEndDate?: any;
	auctionEndTime?: any;
	listingType?: string;
	buyItNowPrice?: string;
}

const editAuctionSchema = Yup.object().shape({
	listingType: Yup.string().required('Listing Type is required'),
	tableId: Yup.string().required('Table is required'),
	tablePrice: Yup.number()
		.when('listingType', {
			is: (listingType: string) => listingType !== 'BUY_IT_NOW_ONLY',
			then: (schema) => schema
				.min(10, 'Price must be greater than $10')
				.required('Price is required')
				.typeError('Price must be a number')
				.max(20000, 'Value must be less than or equal to 20,000'),
			otherwise: (schema) => schema.notRequired()
		}),
	buyItNowPrice: Yup.number()
		.when('listingType', {
			is: (listingType: string) => listingType === 'BUY_IT_NOW_ONLY',
			then: (schema) => schema
				.required('Buy It Now Price is required')
				.typeError('Buy It Now Price must be a number')
				.max(20000, 'Value must be less than or equal to 20,000'),
			otherwise: (schema) => schema.notRequired()
		}),
	dineDate: Yup.date()
		.required('Dine In date is required')
		.test(
			'is-after-auction-start',
			'The date of the dinner must be after the start of the auction.',
			function (dineDate) {
				const { auctionStartDate, listingType } = this.parent;
				if (listingType === 'BUY_IT_NOW_ONLY') return true;
				if (!dineDate || !auctionStartDate) return true;
				return moment(dineDate).isSameOrAfter(auctionStartDate, 'day');
			}
		)
		.test(
			'is-after-auction-end',
			'The dinner date must be on or after the auction end date.',
			function (dineDate) {
				const { auctionEndDate, listingType } = this.parent;
				if (listingType === 'BUY_IT_NOW_ONLY') return true;
				if (!dineDate || !auctionEndDate) return true;
				return moment(dineDate).isSameOrAfter(auctionEndDate, 'day');
			}
		),
	dineTime: Yup.string()
		.required('Dine In Time is required')
		.test(
			'is-after-auction-end-time',
			'Dinner time must be at least 1 hour after the auction end time.',
			function (dineTime) {
				const { dineDate, auctionEndDate, auctionEndTime, listingType } = this.parent;
				if (listingType === 'BUY_IT_NOW_ONLY') return true;
				if (!dineTime || !auctionEndTime || !dineDate || !auctionEndDate) return true;

				// Only validate if the dates are equal
				if (moment(dineDate).isSame(auctionEndDate, 'day')) {
					const dineMoment = moment(dineTime, 'h:mm A');
					const auctionEndMoment = moment(auctionEndTime, 'h:mm A').add(1, 'hours');
					return dineMoment.isSameOrAfter(auctionEndMoment);
				}

				return true;
			}
		),
	auctionType: Yup.string().required('Seating Type is required'),
	auctionStartDate: Yup.date()
		.when('listingType', {
			is: (listingType: string) => listingType !== 'BUY_IT_NOW_ONLY',
			then: (schema) => schema.required('Auction Start Date is required'),
			otherwise: (schema) => schema.notRequired()
		}),
	auctionStartTime: Yup.string()
		.when('listingType', {
			is: (listingType: string) => listingType !== 'BUY_IT_NOW_ONLY',
			then: (schema) => schema.required('Auction Start Time is required'),
			otherwise: (schema) => schema.notRequired()
		}),
	auctionEndDate: Yup.date()
		.when('listingType', {
			is: (listingType: string) => listingType !== 'BUY_IT_NOW_ONLY',
			then: (schema) => schema
				.required('Auction End Date is required')
				.test(
					'is-after-start',
					'Auction end date must be after the start date',
					function (endDate) {
						const { auctionStartDate } = this.parent;
						if (!endDate || !auctionStartDate) return true;
						return moment(endDate).isSameOrAfter(auctionStartDate, 'day');
					}
				),
			otherwise: (schema) => schema.notRequired()
		}),
	auctionEndTime: Yup.string()
		.when('listingType', {
			is: (listingType: string) => listingType !== 'BUY_IT_NOW_ONLY',
			then: (schema) => schema
				.required('Auction End Time is required')
				.test(
					'is-after-start-time',
					'Auction end time must be after the start time',
					function (endTime) {
						const { auctionStartDate, auctionStartTime, auctionEndDate } = this.parent;

						if (!endTime || !auctionStartTime || !auctionEndDate) return true;

						const startMoment = moment(auctionStartDate)
							.set({
								hour: moment(auctionStartTime, 'h:mm A').hours(),
								minute: moment(auctionStartTime, 'h:mm A').minutes(),
							});

						const endMoment = moment(auctionEndDate)
							.set({
								hour: moment(endTime, 'h:mm A').hours(),
								minute: moment(endTime, 'h:mm A').minutes(),
							});

						return endMoment.isAfter(startMoment);
					}
				),
			otherwise: (schema) => schema.notRequired()
		}),
});

const AuctionBody = ({
	auctionData,
	handleSaveCallback,
	handleCancelCallback,
}: {
	auctionData: TAuction | any | null;
	handleSaveCallback: (value: any) => void;
	handleCancelCallback: () => void;
}) => {
	const [auctionDataByApi, setAuctionsDataByApi] = useState<any>();
	const { auctionModule } = useAuth();

	const getAuctionDetails = async () => {
		if (!auctionData?.id) return;
		const result = await auctionModule?.getSingleAuctions(auctionData?.id);
		setAuctionsDataByApi(result?.data);
	};

	useEffect(() => {
		getAuctionDetails();
	}, []);

	// Update form when auctionDataByApi changes
	useEffect(() => {
		if (auctionDataByApi && !formik.dirty) {
			const parseDate = (dateString: string) =>
				moment.utc(dateString).toDate();

			const formatTime = (timeString: string) =>
				moment(timeString, 'HH:mm:ss').format('h:mm A');

			formik.setValues({
				...formik.values,
				auctionStartDate: parseDate(auctionDataByApi.auctionStartDate),
				auctionStartTime: formatTime(auctionDataByApi.auctionStartTime),
				auctionEndDate: parseDate(auctionDataByApi.auctionEndDate),
				auctionEndTime: formatTime(auctionDataByApi.auctionEndTime),
			});
		}
	}, [auctionDataByApi]);

	const initialAuctionData = {
		listingType: 'AUCTION_ONLY',
		tableId: '',
		dineDate: moment().startOf('day').toDate(),
		tablePrice: '',
		buyItNowPrice: '',
		dineTime: '',
		bidsEndAt: '',
		auctionType: '',
		isView: false,
		auctionStartDate: moment().startOf('day').toDate(),
		auctionStartTime: '',
		auctionEndDate: moment().startOf('day').toDate(),
		auctionEndTime: '',
	};

	const [errorMessage, setErrorMessage] = useState('');

	const handleDineTimeChange = (value: string) => {
		const selectedTime = dayjs(value, 'h:mm A');
		const currentTime = dayjs();
		const currentTimePlus4Hours = currentTime.add(1, 'hours');
		const currentDate = dayjs();
		const auctionDate = formik.values.dineDate;
		formik.setFieldValue('dineTime', value);
		const bidsEndAtTime = selectedTime.subtract(1, 'hours').format('h:mm A');
		formik.setFieldValue('bidsEndAt', bidsEndAtTime);

		if (currentDate.isSame(auctionDate, 'day')) {
			if (selectedTime.isBefore(currentTime)) {
				setErrorMessage(`You cannot select a previous time for the current day`);
			} else if (selectedTime.isBefore(currentTimePlus4Hours)) {
				setErrorMessage(`Dine-In time should be greater than 1 hours from the current time.`);
			} else {
				setErrorMessage('');
			}
		} else {
			setErrorMessage('');
		}
	};

	const onChange: TimePickerProps['onChange'] = (time: any, timeString: any) => {
		handleDineTimeChange(timeString);
	};

	const parseAuctionData = (data: TAuction) => {
		const parseDate = (date: any) => {
			if (!date || date === "1970-01-01T00:00:00.000Z") return null;
			return moment.utc(date).toDate();
		};

		return {
			...data,
			auctionStartDate: parseDate(data.auctionStartDate),
			auctionEndDate: parseDate(data.auctionEndDate),
			dineDate: parseDate(data.dineDate),
		};
	};

	const formik = useFormik({
		initialValues: (auctionData && auctionData) || initialAuctionData,
		validationSchema: editAuctionSchema,
		enableReinitialize: true,
		onSubmit: async (values) => {
			formik.setSubmitting(true);

			const formatTimeTo24H = (time: string) => {
				const [timePart, modifier] = time.split(' ');
				let [hours, minutes] = timePart.split(':').map(Number);

				if (modifier === 'PM' && hours < 12) hours += 12;
				if (modifier === 'AM' && hours === 12) hours = 0;

				return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
			};

			let auctionData: any = {
				tableId: values.tableId,
				dineDate: moment(createDateWithTime(values.dineDate, values.dineTime)).utc().format(),
				auctionTypeId: values.auctionType,
				isView: values.isView,
				listingType: values.listingType,
			};

			// Add pricing fields based on listing type
			if (values.listingType !== 'BUY_IT_NOW_ONLY') {
				auctionData.price = values.tablePrice;
			}

			if (values.listingType === 'BUY_IT_NOW_ONLY') {
				auctionData.buyItNowPrice = values.buyItNowPrice;
			} else if (values.listingType === 'AUCTION_BUY_IT_NOW') {
				auctionData.buyItNowPrice = parseFloat(values.tablePrice) * 1.25;
			}

			const auctionStartDateTime = createDateWithTime(
				values.auctionStartDate,
				values.auctionStartTime
			);

			const auctionEndDateTime = createDateWithTime(
				values.auctionEndDate,
				values.auctionEndTime
			);

			auctionData = {
				...auctionData,
				endDate: moment(auctionEndDateTime).utc().format(),
				auctionStartDate: moment(auctionStartDateTime).utc().format(),
				auctionStartTime: formatTimeTo24H(values.auctionStartTime),
				auctionEndDate: moment(auctionEndDateTime).utc().format(),
				auctionEndTime: formatTimeTo24H(values.auctionEndTime),
				bidsEndAt: moment(auctionEndDateTime).subtract(4, 'hours').utc().format(),
			};

			await handleSaveCallback(auctionData);
			formik.setSubmitting(false);
		},
	});

	// Listing type options
	const listingTypeOptions = [
		{ value: 'AUCTION_ONLY', label: 'Auction Only' },
		{ value: 'AUCTION_BUY_IT_NOW', label: 'Auction + Buy It Now' },
		{ value: 'BUY_IT_NOW_ONLY', label: 'Buy It Now Only' }
	];

	return (
		<>
			<form id='kt_modal_add_user_form' className='form' onSubmit={formik.handleSubmit} noValidate>
				<div
					className='d-flex flex-column me-n7 pe-7'
					id='kt_modal_add_user_scroll'
					data-kt-scroll='true'
					data-kt-scroll-activate='{default: false, lg: true}'
					data-kt-scroll-max-height='auto'
					data-kt-scroll-dependencies='#kt_modal_add_user_header'
					data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
					data-kt-scroll-offset='300px'
				>
					{/* Listing Type Dropdown */}
					<div className='fv-row mb-4'>
						<label className='required fw-bold fs-6 mb-2'>Listing Type</label>
						<select
							className='form-select form-select-solid'
							{...formik.getFieldProps('listingType')}
							disabled={auctionData?.isView === true}
						>
							{listingTypeOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
						{formik.touched.listingType && formik.errors.listingType && (
							<div className='fv-plugins-message-container'>
								<div className='fv-help-block'>
									<span role='alert'>{formik.errors.listingType as string}</span>
								</div>
							</div>
						)}
					</div>

					<div className='fv-row row mb-4'>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Table</label>
							<TableSelection
								onblur={() => {
									formik.setFieldTouched('tableId', true);
								}}
								as='select'
								{...formik.getFieldProps('tableId')}
								onChange={(value) => {
									formik.setFieldValue('tableId', value.value);
								}}
								name='tableId'
								value={formik.getFieldProps('tableId').value}
								isDisabled={auctionData?.isView === true}
							/>
							{formik.touched.tableId && formik.errors.tableId && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.tableId as string}</span>
									</div>
								</div>
							)}
						</div>

						{/* Starting Bid - Show for Auction Only and Auction + Buy It Now */}
						{formik.values.listingType !== 'BUY_IT_NOW_ONLY' ? (
							<div className='col-md-6 mb-4'>
								<label className='required fw-bold fs-6 mb-2'>Starting Bid</label>
								<div className='input-group'>
									<div className='input-group-prepend'>
										<span className='input-group-text input-prepand-text '>$</span>
									</div>
									<input
										placeholder='Starting Bid'
										{...formik.getFieldProps('tablePrice')}
										type='number'
										name='tablePrice'
										className='form-control form-control-solid mb-3 mb-lg-0'
										autoComplete='off'
										disabled={formik.isSubmitting}
										onKeyDown={(evt) => (evt.key === 'e' || evt.key === '-') && evt.preventDefault()}
									/>
								</div>
								{formik.touched.tablePrice && formik.errors.tablePrice && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert'>{formik.errors.tablePrice as string}</span>
										</div>
									</div>
								)}
							</div>
						): (
				  <div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Buy It Now Price</label>
							<div className='input-group'>
								<div className='input-group-prepend'>
									<span className='input-group-text input-prepand-text '>$</span>
								</div>
								<input
									placeholder='Buy It Now Price'
									{...formik.getFieldProps('buyItNowPrice')}
									type='number'
									name='buyItNowPrice'
									className='form-control form-control-solid mb-3 mb-lg-0'
									autoComplete='off'
									disabled={formik.isSubmitting}
									onKeyDown={(evt) => (evt.key === 'e' || evt.key === '-') && evt.preventDefault()}
								/>
							</div>
							{formik.touched.buyItNowPrice && formik.errors.buyItNowPrice && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.buyItNowPrice as string}</span>
									</div>
								</div>
							)}
						</div>
						)}
					</div>



					{/* Auction Start/End Date/Time - Hide for Buy It Now Only */}

						<>
							<div className='fv-row row mb-4 date-width'>
								<div className='col-md-6 mb-4'>
									<label className='required fw-bold fs-6 mb-2'>Auction Start Date</label>
									<DatePicker
										autoComplete='off'
										selected={formik.values.auctionStartDate}
										dateFormat='MM/dd/yy'
										placeholder='Auction Start Date'
										onBlur={() => formik.setFieldTouched('auctionStartDate', true)}
										className='form-control form-control-lg form-control-solid'
										onChange={(date) => {
											formik.setFieldValue('auctionStartDate', date);
										}}
										popperPlacement='bottom'
										shouldCloseOnSelect={false}
										minDate={new Date()}
									/>
									{formik.touched.auctionStartDate && formik.errors.auctionStartDate && (
										<div className='fv-plugins-message-container'>
											<div className='fv-help-block'>
												<span role='alert'>{formik.errors.auctionStartDate as string}</span>
											</div>
										</div>
									)}
								</div>
								<div className='col-md-6 mb-4'>
									<label className='required fw-bold fs-6 mb-2'>Auction Start Time</label>
									<TimePicker
										use12Hours
										value={formik.values.auctionStartTime ? dayjs(formik.values.auctionStartTime, 'h:mm A') : null}
										format='h:mm A'
										allowClear={false}
										needConfirm={false}
										onChange={(time, timeString) => {
											formik.setFieldValue('auctionStartTime', timeString);
										}}
										className='form-control form-control-lg form-control-solid'
										disabledTime={(current) => {
											if (dayjs(formik.values.auctionStartDate).isSame(dayjs(), 'day')) {
												const now = dayjs();
												return {
													disabledHours: () => {
														const hours: number[] = [];
														for (let i = 0; i < now.hour(); i++) {
															hours.push(i);
														}
														return hours;
													},
													disabledMinutes: (selectedHour) => {
														if (selectedHour === now.hour()) {
															const minutes: number[] = [];
															for (let i = 0; i < now.minute(); i++) {
																minutes.push(i);
															}
															return minutes;
														}
														return [];
													}
												};
											}
											return {};
										}}
									/>
									{formik.touched.auctionStartTime && formik.errors.auctionStartTime && (
										<div className='fv-plugins-message-container'>
											<div className='fv-help-block'>
												<span role='alert'>{formik.errors.auctionStartTime as string}</span>
											</div>
										</div>
									)}
								</div>
							</div>

							<div className='fv-row row mb-4 date-width'>
								<div className='col-md-6 mb-4'>
									<label className='required fw-bold fs-6 mb-2'>Auction End Date</label>
									<DatePicker
										autoComplete='off'
										selected={formik.values.auctionEndDate ? formik.values.auctionEndDate : moment().startOf('day').toDate()}
										dateFormat='MM/dd/yy'
										placeholder='Auction End Date'
										onBlur={() => formik.setFieldTouched('auctionEndDate', true)}
										className='form-control form-control-lg form-control-solid'
										onChange={(date) => {
											formik.setFieldValue('auctionEndDate', date);
										}}
										popperPlacement='bottom'
										shouldCloseOnSelect={false}
										minDate={new Date()}
									/>
									{formik.touched.auctionEndDate && formik.errors.auctionEndDate && (
										<div className='fv-plugins-message-container'>
											<div className='fv-help-block'>
												<span role='alert'>{formik.errors.auctionEndDate as string}</span>
											</div>
										</div>
									)}
								</div>
								<div className='col-md-6 mb-4'>
									<label className='required fw-bold fs-6 mb-2'>Auction End Time</label>
									<TimePicker
										use12Hours
										value={formik.values.auctionEndTime ? dayjs(formik.values.auctionEndTime, 'h:mm A') : null}
										format='h:mm A'
										allowClear={false}
										needConfirm={false}
										onChange={(time, timeString) => {
											formik.setFieldValue('auctionEndTime', timeString);
										}}
										className='form-control form-control-lg form-control-solid'
										disabledTime={(current) => {
											const now = dayjs();
											const endDate = dayjs(formik.values.auctionEndDate);
											const startDate = dayjs(formik.values.auctionStartDate);
											const startTime = formik.values.auctionStartTime;

											// If end date is today, disable past times
											if (endDate.isSame(now, 'day')) {
												return {
													disabledHours: () => {
														const hours: number[] = [];
														for (let i = 0; i < now.hour(); i++) {
															hours.push(i);
														}
														return hours;
													},
													disabledMinutes: (selectedHour) => {
														if (selectedHour === now.hour()) {
															const minutes: number[] = [];
															for (let i = 0; i < now.minute(); i++) {
																minutes.push(i);
															}
															return minutes;
														}
														return [];
													}
												};
											}

											// If start and end dates are the same, ensure end time is after start time
											if (startDate.isSame(endDate, 'day') && startTime) {
												const startMoment = dayjs(startTime, 'h:mm A');
												return {
													disabledHours: () => {
														const hours: number[] = [];
														// Disable hours before start time
														for (let i = 0; i < startMoment.hour(); i++) {
															hours.push(i);
														}
														return hours;
													},
													disabledMinutes: (selectedHour) => {
														if (selectedHour === startMoment.hour()) {
															const minutes: number[] = [];
															// Disable minutes up to and including start time minute
															for (let i = 0; i <= startMoment.minute(); i++) {
																minutes.push(i);
															}
															return minutes;
														}
														return [];
													}
												};
											}

											return {};
										}}
									/>
									{formik.touched.auctionEndTime && formik.errors.auctionEndTime && (
										<div className='fv-plugins-message-container'>
											<div className='fv-help-block'>
												<span role='alert'>{formik.errors.auctionEndTime as string}</span>
											</div>
										</div>
									)}
								</div>
							</div>
						</>


					<div className='fv-row row mb-4 date-width'>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Dine In Date</label>
							<DatePicker
								autoComplete='off'
								selected={formik.values.dineDate}
								dateFormat='MM/dd/yy'
								placeholder='Dining Date & Time'
								onBlur={() => formik.setFieldTouched('dineDate', true)}
								className='form-control form-control-lg form-control-solid'
								onChange={(date) => {
									formik.setFieldValue('dineDate', date);
								}}
								popperPlacement='bottom'
								shouldCloseOnSelect={false}
								minDate={new Date()}
							/>
							{formik.touched.dineDate && formik.errors.dineDate && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.dineDate as string}</span>
									</div>
								</div>
							)}
						</div>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Dine In Time</label>
							<TimePicker
								use12Hours
								value={formik?.values?.dineTime ? dayjs(formik?.values?.dineTime, 'h:mm A') : null}
								format='h:mm A'
								allowClear={false}
								needConfirm={false}
								onChange={onChange}
								className='form-control form-control-lg form-control-solid'
							/>
							{errorMessage && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{errorMessage}</span>
									</div>
								</div>
							)}
							{formik.touched.dineTime && formik.errors.dineTime && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.dineTime as string}</span>
									</div>
								</div>
							)}
						</div>
					</div>

					<div className='fv-row row mb-4 date-width'>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Seating Type</label>
							<AuctionTypeSelection
								onblur={() => {
									formik.setFieldTouched('auctionType', true);
								}}
								as='select'
								{...formik.getFieldProps('auctionType')}
								onChange={(value) => {
									formik.setFieldValue('auctionType', value.value);
								}}
								name='auctionType'
								value={formik.getFieldProps('auctionType').value}
							/>
							{formik.touched.auctionType && formik.errors.auctionType && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.auctionType as string}</span>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Actions */}
					<div className='text-center pt-15'>
						<button
							type='reset'
							onClick={handleCancelCallback}
							className='btn btn-light me-3'
							data-kt-users-modal-action='cancel'
							disabled={formik.isSubmitting}
						>
							Discard
						</button>

						<button
							type='submit'
							className='btn btn-primary'
							data-kt-users-modal-action='submit'
							disabled={formik.isSubmitting}
						>
							{!formik.isSubmitting && 'Submit'}
							{formik.isSubmitting && (
								<span className='indicator-progress' style={{ display: 'block' }}>
									Please wait...{' '}
									<span className='spinner-border spinner-border-sm align-middle ms-2'></span>
								</span>
							)}
						</button>
					</div>
				</div>
			</form>
		</>
	);
};

export default AuctionBody;
